import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../../../utilities/appToast';
import { getLiquidacionesBusqueda, getLiquidacionporid } from '../../../../api/axios';
import BigSpinner from '../../../General/BigSpinner';
import SafeReactPaginate from '../../../General/SafeReactPaginate';
import '../../shared/liquidacionesModule.css';
import LiquidacionesPageHeader from '../../shared/LiquidacionesPageHeader';
import LiquidacionesFilterCard from '../../shared/LiquidacionesFilterCard';
import LiquidacionesFilterActions from '../../shared/LiquidacionesFilterActions';
import { LiquidacionesEmptyState } from '../../shared/LiquidacionesButtons';
import { LIQUIDACIONES_ROUTES } from '../../shared/liquidacionesRoutes';
import { fechaLimite, filtroMedioMes } from '../../../../utilities/fechasFiltro';
import {
    buildLiquidacionPipelineMonitorModel,
    getLiquidacionPipelineSnapshot,
    getPipelineDisplaySummary,
    PIPELINE_STATUS_LABELS
} from '../../../../helper/liquidacionPipelineMonitorHelper';
import './LiquidacionesPipelinesDashboard.css';

const fechaFiltro = filtroMedioMes();
const fechaDesdeMinima = fechaLimite();
const DEFAULT_PAGE_SIZE = 12;

const VIEW_FILTERS = [
    { key: 'todos', label: 'Todos' },
    { key: 'activos', label: 'En gestion' },
    { key: 'finalizados', label: 'Finalizados' },
    { key: 'errores', label: 'Con error' }
];

const AUTHORIZED_VIEW_ALL = [1, 2, 8];

const normalizeLiquidacionDetalle = (value) => {
    const detail = value || {};

    return {
        ...detail,
        encabezadoLiquidacion: detail?.encabezadoLiquidacion ?? detail?.EncabezadoLiquidacion ?? {},
        cuerpoOrdenLiquidaciones: detail?.cuerpoOrdenLiquidaciones ?? detail?.CuerpoOrdenLiquidaciones ?? [],
        cuerpoNovedadLiquidaciones: detail?.cuerpoNovedadLiquidaciones ?? detail?.CuerpoNovedadLiquidaciones ?? [],
        cuerpoFeeLogisticoLiquidaciones: detail?.cuerpoFeeLogisticoLiquidaciones ?? detail?.CuerpoFeeLogisticoLiquidaciones ?? [],
        detalleRebate: detail?.detalleRebate ?? detail?.DetalleRebate ?? []
    };
};

const parseIdLiquidacion = (rawValue) => {
    const normalized = String(rawValue ?? '').trim();
    if (!normalized) {
        return null;
    }

    const parsedValue = Number.parseInt(normalized, 10);
    return Number.isNaN(parsedValue) || parsedValue < 1 ? null : parsedValue;
};

const resolveSellerId = (...values) => {
    for (const value of values) {
        const parsedValue = Number.parseInt(value, 10);
        if (Number.isFinite(parsedValue) && parsedValue > 0) {
            return parsedValue;
        }
    }

    return undefined;
};

const sortPipelines = (pipelines = []) => {
    return [...pipelines].sort((left, right) => {
        if (left.statusRank !== right.statusRank) {
            return left.statusRank - right.statusRank;
        }

        return Number(right.idLiquidacion || 0) - Number(left.idLiquidacion || 0);
    });
};

const parseBusquedaResponse = (response, request) => {
    const numeroPagina = Number(request?.numeroPagina ?? 1) || 1;
    const tamanoPagina = Number(request?.tamanoPagina ?? DEFAULT_PAGE_SIZE) || DEFAULT_PAGE_SIZE;
    const lista = Array.isArray(response?.encabezado)
        ? response.encabezado
        : Array.isArray(response?.Encabezado)
            ? response.Encabezado
            : [];
    const ordenadas = [...lista]
        .sort((left, right) => Number(right?.idLiquidacion || 0) - Number(left?.idLiquidacion || 0));
    const lenAntesSlice = ordenadas.length;
    const pagina = ordenadas.slice(0, tamanoPagina);

    let cantidadTotal = response?.cantidadTotal ?? response?.CantidadTotal ?? response?.totalRegistros
        ?? response?.TotalRegistros ?? response?.total ?? response?.Total
        ?? response?.cantidadRegistros ?? response?.CantidadRegistros
        ?? response?.totalItems ?? response?.TotalItems;

    if (cantidadTotal == null || cantidadTotal === '' || Number.isNaN(Number(cantidadTotal))) {
        if (lenAntesSlice < tamanoPagina) {
            cantidadTotal = (Math.max(1, numeroPagina) - 1) * tamanoPagina + lenAntesSlice;
        } else {
            cantidadTotal = numeroPagina * tamanoPagina + 1;
        }
    }

    return {
        lista: pagina,
        cantidadTotal: Number(cantidadTotal)
    };
};

function LiquidacionesPipelinesDashboard({ tipoUsuario, idSeller }) {
    const navigate = useNavigate();
    const [fechaDesde, setFechaDesde] = useState(fechaFiltro.desde);
    const [fechaHasta, setFechaHasta] = useState(fechaFiltro.hasta);
    const [idLiquidacion, setIdLiquidacion] = useState('');
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [numeroPagina, setNumeroPagina] = useState(1);
    const [cantidadTotal, setCantidadTotal] = useState(0);
    const [filtroVista, setFiltroVista] = useState('todos');
    const [liquidaciones, setLiquidaciones] = useState([]);
    const [detallesPorId, setDetallesPorId] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingDetalles, setIsLoadingDetalles] = useState(false);
    const [ultimaActualizacion, setUltimaActualizacion] = useState('');
    const isAuthorizedViewAll = AUTHORIZED_VIEW_ALL.includes(tipoUsuario);

    const cargarMonitor = async (overrides = {}) => {
        const nextFechaDesde = overrides.fechaDesde ?? fechaDesde;
        const nextFechaHasta = overrides.fechaHasta ?? fechaHasta;
        const nextIdLiquidacion = overrides.idLiquidacion ?? idLiquidacion;
        const nextPageSize = overrides.pageSize ?? pageSize;
        const nextNumeroPagina = overrides.numeroPagina ?? numeroPagina;
        const parsedIdLiquidacion = parseIdLiquidacion(nextIdLiquidacion);
        const request = {
            fechaDesde: nextFechaDesde,
            fechaHasta: nextFechaHasta,
            numeroPagina: nextNumeroPagina,
            tamanoPagina: nextPageSize
        };

        if (parsedIdLiquidacion != null) {
            request.idLiquidacion = parsedIdLiquidacion;
        }

        if (!isAuthorizedViewAll) {
            request.idSeller = idSeller;
        }

        setIsLoading(true);

        try {
            const response = await getLiquidacionesBusqueda(request);
            const { lista: ordenadas, cantidadTotal: total } = parseBusquedaResponse(response, request);

            setLiquidaciones(ordenadas);
            setCantidadTotal(total);
            setNumeroPagina(nextNumeroPagina);
            setPageSize(nextPageSize);
            setUltimaActualizacion(new Date().toLocaleString('es-AR'));
            setIsLoading(false);

            if (ordenadas.length === 0) {
                setDetallesPorId({});
                return;
            }

            setIsLoadingDetalles(true);
            const detalleResultados = await Promise.allSettled(
                ordenadas.map((liquidacion) => {
                    const detailRequest = {
                        idLiquidacion: liquidacion.idLiquidacion,
                        borrador: Number(liquidacion?.idTipoEstadoLiquidacion) === 2 ? 1 : 0
                    };
                    const resolvedSellerId = resolveSellerId(liquidacion?.idSeller, liquidacion?.IdSeller, !isAuthorizedViewAll ? idSeller : undefined);

                    if (resolvedSellerId) {
                        detailRequest.idSeller = resolvedSellerId;
                    }

                    return getLiquidacionporid(detailRequest);
                })
            );

            const detalleMap = detalleResultados.reduce((acc, result, index) => {
                if (result.status === 'fulfilled') {
                    acc[ordenadas[index].idLiquidacion] = normalizeLiquidacionDetalle(result.value);
                }

                return acc;
            }, {});

            setDetallesPorId(detalleMap);
        } catch (error) {
            setLiquidaciones([]);
            setDetallesPorId({});
            setCantidadTotal(0);
            toast.error('No pudimos cargar el monitor de pipelines de liquidaciones.');
        } finally {
            setIsLoading(false);
            setIsLoadingDetalles(false);
        }
    };

    useEffect(() => {
        cargarMonitor();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pipelines = useMemo(() => {
        const models = liquidaciones.map((liquidacion) => {
            const detalle = detallesPorId[liquidacion.idLiquidacion] || {};
            const encabezado = detalle?.encabezadoLiquidacion || {};
            const snapshot = getLiquidacionPipelineSnapshot(liquidacion.idLiquidacion);

            const liquidacionCompleta = {
                ...liquidacion,
                ...encabezado,
                ...detalle,
                idLiquidacion: liquidacion.idLiquidacion,
                idSeller: resolveSellerId(
                    liquidacion?.idSeller,
                    liquidacion?.IdSeller,
                    encabezado?.idSeller,
                    encabezado?.IdSeller,
                    detalle?.idSeller,
                    detalle?.IdSeller,
                    !isAuthorizedViewAll ? idSeller : undefined
                ),
                razonSocialSeller: liquidacion.razonSocialSeller || encabezado.razonSocialSeller,
                mostrarFecha: liquidacion.mostrarFecha || encabezado.mostrarFecha,
                cantidadOrdenes: liquidacion.cantidadOrdenes ?? encabezado.cantidadOrdenes ?? detalle.cuerpoOrdenLiquidaciones?.length,
                cantidadNovedades: liquidacion.cantidadNovedades ?? encabezado.cantidadNovedades ?? detalle.cuerpoNovedadLiquidaciones?.length
            };

            return buildLiquidacionPipelineMonitorModel(liquidacionCompleta, snapshot);
        });

        return sortPipelines(models);
    }, [detallesPorId, idSeller, isAuthorizedViewAll, liquidaciones]);

    const metricas = useMemo(() => {
        return pipelines.reduce((acc, pipeline) => {
            acc.todos += 1;

            if (pipeline.monitorGroup === 'finalizados') {
                acc.finalizados += 1;
            } else {
                acc.activos += 1;
            }

            if (pipeline.overallStatus === 'failed') {
                acc.errores += 1;
            }

            if (pipeline.overallStatus === 'paused') {
                acc.pausados += 1;
            }

            return acc;
        }, {
            todos: 0,
            activos: 0,
            finalizados: 0,
            errores: 0,
            pausados: 0
        });
    }, [pipelines]);

    const pipelinesFiltrados = useMemo(() => {
        if (filtroVista === 'todos') {
            return pipelines;
        }

        if (filtroVista === 'errores') {
            return pipelines.filter((pipeline) => pipeline.overallStatus === 'failed');
        }

        return pipelines.filter((pipeline) => pipeline.monitorGroup === filtroVista);
    }, [filtroVista, pipelines]);

    const handleOpenPipeline = (pipeline) => {
        const liquidacionBase = liquidaciones.find((item) => item.idLiquidacion === pipeline.idLiquidacion) || {};

        navigate(LIQUIDACIONES_ROUTES.pipelineDetalle(pipeline.idLiquidacion), {
            state: {
                idSeller: pipeline.idSeller,
                borrador: Number(liquidacionBase?.idTipoEstadoLiquidacion) === 2 ? 1 : 0,
                idTipoEstadoLiquidacion: liquidacionBase?.idTipoEstadoLiquidacion,
                razonSocialSeller: liquidacionBase.razonSocialSeller,
                mostrarFecha: liquidacionBase.mostrarFecha,
                cantidadOrdenes: liquidacionBase.cantidadOrdenes,
                cantidadNovedades: liquidacionBase.cantidadNovedades
            }
        });
    };

    const pageCount = Math.max(1, Math.ceil((cantidadTotal || 0) / pageSize));
    const rangoDesde = cantidadTotal === 0 ? 0 : (numeroPagina - 1) * pageSize + 1;
    const rangoHasta = cantidadTotal === 0 ? 0 : Math.min(numeroPagina * pageSize, cantidadTotal);

    const handlePageClick = (data) => {
        const selectedPage = data.selected + 1;
        setNumeroPagina(selectedPage);
        cargarMonitor({ numeroPagina: selectedPage });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setNumeroPagina(1);
        cargarMonitor({ numeroPagina: 1 });
    };

    const handleClear = (event) => {
        event.preventDefault();
        setFechaDesde(fechaFiltro.desde);
        setFechaHasta(fechaFiltro.hasta);
        setIdLiquidacion('');
        setPageSize(DEFAULT_PAGE_SIZE);
        setNumeroPagina(1);
        setFiltroVista('todos');
        cargarMonitor({
            fechaDesde: fechaFiltro.desde,
            fechaHasta: fechaFiltro.hasta,
            idLiquidacion: '',
            pageSize: DEFAULT_PAGE_SIZE,
            numeroPagina: 1
        });
    };

    return (
        <div className="container liquidaciones-pipelines-dashboard gestion-liquidaciones liquidaciones-modulo">
            <LiquidacionesPageHeader
                title="Pipelines de liquidaciones"
                subtitle="Monitoreo operativo de las últimas liquidaciones generadas. Haga click en una tarjeta para ver el detalle completo del circuito."
            />

            <form className="form-horizontal form-material gestion-liquidaciones-form" onSubmit={handleSubmit} onReset={handleClear}>
                <LiquidacionesFilterCard
                    actions={(
                        <LiquidacionesFilterActions
                            disabled={isLoading}
                            searchLabel="Actualizar"
                            showSearchIcon={false}
                        />
                    )}
                >
                        <div className="row g-3 align-items-end">
                            <div className="col-12 col-md-2">
                                <p className="gestion-liquidaciones-filtro-label mb-0">Desde</p>
                                <input
                                    type="date"
                                    name="fechaDesde"
                                    className="form-control"
                                    min={fechaDesdeMinima}
                                    value={fechaDesde}
                                    onChange={(event) => setFechaDesde(event.target.value)}
                                />
                            </div>

                            <div className="col-12 col-md-2">
                                <p className="gestion-liquidaciones-filtro-label mb-0">Hasta</p>
                                <input
                                    type="date"
                                    name="fechaHasta"
                                    className="form-control"
                                    min={fechaDesde}
                                    value={fechaHasta}
                                    onChange={(event) => setFechaHasta(event.target.value)}
                                />
                            </div>

                            <div className="col-12 col-md-2">
                                <p className="gestion-liquidaciones-filtro-label mb-0">N° liquidación</p>
                                <input
                                    type="text"
                                    name="idLiquidacion"
                                    className="form-control"
                                    autoComplete="off"
                                    inputMode="numeric"
                                    value={idLiquidacion}
                                    onChange={(event) => setIdLiquidacion(event.target.value)}
                                />
                            </div>

                            <div className="col-12 col-md-2">
                                <p className="gestion-liquidaciones-filtro-label mb-0">Por página</p>
                                <select
                                    className="form-control"
                                    value={pageSize}
                                    onChange={(event) => setPageSize(Number.parseInt(event.target.value, 10))}
                                >
                                    <option value={6}>6 por página</option>
                                    <option value={9}>9 por página</option>
                                    <option value={12}>12 por página</option>
                                    <option value={15}>15 por página</option>
                                </select>
                            </div>

                        </div>
                </LiquidacionesFilterCard>
            </form>

            <div className="liquidaciones-pipelines-dashboard__metrics">
                {VIEW_FILTERS.map((filter) => (
                    <button
                        key={filter.key}
                        type="button"
                        className={[
                            'liquidaciones-pipelines-dashboard__metric',
                            filtroVista === filter.key ? 'liquidaciones-pipelines-dashboard__metric--active' : ''
                        ].join(' ').trim()}
                        onClick={() => setFiltroVista(filter.key)}
                    >
                        <strong>
                            {filter.key === 'todos' && metricas.todos}
                            {filter.key === 'activos' && metricas.activos}
                            {filter.key === 'finalizados' && metricas.finalizados}
                            {filter.key === 'errores' && metricas.errores}
                        </strong>
                        <span>{filter.label}</span>
                    </button>
                ))}
            </div>

            <div className="liquidaciones-pipelines-dashboard__info">
                <div>
                    <strong>{metricas.pausados}</strong>
                    <span> pipelines pausados detectados en sesión</span>
                    {cantidadTotal > 0 && (
                        <span className="liquidaciones-pipelines-dashboard__info-range">
                            {' '}| Mostrando {rangoDesde}-{rangoHasta} de {cantidadTotal}
                        </span>
                    )}
                </div>
                {ultimaActualizacion && <span>Última actualización: {ultimaActualizacion}</span>}
            </div>

            {isLoading ? (
                <BigSpinner message="Cargando pipelines de liquidaciones..." />
            ) : pipelinesFiltrados.length === 0 ? (
                <LiquidacionesEmptyState>
                    No se encontraron pipelines para el filtro seleccionado.
                </LiquidacionesEmptyState>
            ) : (
                <>
                    {isLoadingDetalles && (
                        <div className="alert alert-light liquidaciones-pipelines-dashboard__loading-note" role="status">
                            Refinando el estado de cada pipeline con el detalle completo de las liquidaciones.
                        </div>
                    )}

                    <div className="liquidaciones-pipelines-dashboard__grid">
                        {pipelinesFiltrados.map((pipeline) => {
                            const summary = getPipelineDisplaySummary(pipeline);
                            const statusLabel = PIPELINE_STATUS_LABELS[pipeline.overallStatus] || summary.statusLabel;

                            return (
                                <button
                                    key={pipeline.idLiquidacion}
                                    type="button"
                                    className={[
                                        'liquidaciones-pipelines-dashboard__card',
                                        `liquidaciones-pipelines-dashboard__card--${pipeline.overallStatus}`
                                    ].join(' ').trim()}
                                    onClick={() => handleOpenPipeline(pipeline)}
                                >
                                    <div className="liquidaciones-pipelines-dashboard__card-top">
                                        <span className="liquidaciones-pipelines-dashboard__card-eyebrow">{pipeline.eyebrow}</span>
                                        <span className={`liquidaciones-pipelines-dashboard__card-pill liquidaciones-pipelines-dashboard__card-pill--${pipeline.overallStatus}`}>
                                            {statusLabel}
                                        </span>
                                    </div>

                                    <strong className="liquidaciones-pipelines-dashboard__card-title">{pipeline.title}</strong>
                                    {pipeline.subtitle && (
                                        <span className="liquidaciones-pipelines-dashboard__card-subtitle">{pipeline.subtitle}</span>
                                    )}

                                    <div className="liquidaciones-pipelines-dashboard__card-progress-wrap">
                                        <div className="liquidaciones-pipelines-dashboard__card-progress-ring" style={{ '--progress': summary.overallProgress }}>
                                            <span>{summary.overallProgress}%</span>
                                        </div>
                                        <div className="liquidaciones-pipelines-dashboard__card-progress-copy">
                                            <div className="liquidaciones-pipelines-dashboard__card-track">
                                                <div
                                                    className={`liquidaciones-pipelines-dashboard__card-fill liquidaciones-pipelines-dashboard__card-fill--${pipeline.overallStatus}`}
                                                    style={{ width: `${summary.overallProgress}%` }}
                                                />
                                            </div>
                                            <small>{summary.completedCount} de {summary.totalCount} etapas</small>
                                        </div>
                                    </div>

                                    {pipeline.errorMessage && (
                                        <div className="liquidaciones-pipelines-dashboard__card-error">
                                            {pipeline.errorMessage}
                                        </div>
                                    )}

                                    <span className="liquidaciones-pipelines-dashboard__card-cta">Ver detalle completo</span>
                                </button>
                            );
                        })}
                    </div>

                    {pageCount > 1 && (
                        <div className="gestion-liquidaciones-pagination-wrap liquidaciones-pipelines-dashboard__pagination">
                            <SafeReactPaginate
                                previousLabel="Anterior"
                                nextLabel="Siguiente"
                                breakLabel="..."
                                breakClassName="break-me"
                                pageCount={pageCount}
                                marginPagesDisplayed={2}
                                pageRangeDisplayed={5}
                                onPageChange={handlePageClick}
                                containerClassName="pagination liquidacion-detalle-pagination mb-0"
                                subContainerClassName="pages pagination"
                                activeClassName="active"
                                forcePage={numeroPagina - 1}
                                disableInitialCallback
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

const mapStateToProps = (state) => ({
    tipoUsuario: state.general.tipoUsuario,
    idSeller: state.general.idSeller
});

export default connect(mapStateToProps)(LiquidacionesPipelinesDashboard);
